import Fab from "@material-ui/core/Fab";
import Link from "@material-ui/core/Link";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import ConfirmationDialog from "@components/ConfirmationDialog";
import CrudTable from "@components/CrudTable";
import PageBody from "@components/PageBody";
import GroupService from "@services/Group";
import AdminPageTemplate from "@templates/AdminPage";

const useStyles = makeStyles(() => ({
  linkCell: {
    textDecoration: "none !important",
    color: "#3f51b5 !important",
    cursor: "pointer",
  },
  control: {
    position: "fixed",
    bottom: "16px",
    right: "52px",
    "@media all and (min-width: 1024px)": {
      right: "65px",
    },
  },
}));

const defaultConfirmationDialogData = {
  open: false,
  title: "",
  message: "",
  onClose: () => {},
  onDisagree: () => {},
  onAgree: () => {},
};

function Groups(props) {
  const classes = useStyles();
  const history = useHistory();

  const accountId = useSelector((state) => state.account.id);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmationDialogData, setConfirmationDialogData] = useState(
    defaultConfirmationDialogData
  );

  const columns = [
    {
      id: "id",
      label: "Código de Identificação",
      width: 220,
      render: renderIDCell,
    },
    {
      id: "name",
      label: "Nome",
      align: "left",
    },
    {
      id: "menu",
      type: "menu",
      label: "",
      width: 48,
      align: "right",
    },
  ];

  function renderIDCell(id) {
    return (
      <Link className={classes.linkCell} onClick={() => handleClickRowID(id)}>
        {id}
      </Link>
    );
  }

  function loadGroups() {
    setLoading(true);
    setRows([]);
    setSelectedRows([]);

    GroupService.list(accountId)
      .then((data) => {
        setRows(createRows(data));
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Erro ao carregar lista de grupos!");
        setLoading(false);
      });
  }

  function deleteGroup(listId) {
    GroupService.delete(listId)
      .then(() => {
        toast.success(`Grupo "${listId}" excluído!`);
        loadGroups();
      })
      .catch((e) => {
        console.error(e);
        toast.error(`Erro ao excluir grupo "${listId}"!`);
      });
  }

  function deleteMultipleGroups(rows) {
    const requests = rows.map((row) => GroupService.delete(row.id));
    Promise.all(requests)
      .then(() => {
        toast.success("Grupos excluídos!");
        loadGroups();
      })
      .catch((e) => {
        console.error(e);
        toast.error("Erro ao excluir grupos selecionados!");
      });
  }

  function createMenu() {
    return [
      {
        label: "Editar",
        icon: EditIcon,
        onClick: handleClickEditRow,
      },
      {
        label: "Excluir",
        icon: DeleteIcon,
        onClick: handleClickDeleteRow,
      },
    ];
  }

  function createRows(data) {
    return data.map((group) => ({
      id: group.listId,
      name: group.name,
      menu: createMenu(),
    }));
  }

  function closeConfirmationDialog() {
    setConfirmationDialogData({ ...confirmationDialogData, open: false });
    setTimeout(() => {
      setConfirmationDialogData(defaultConfirmationDialogData);
    }, 500);
  }

  function showDeleteConfirmation(row) {
    setConfirmationDialogData({
      open: true,
      title: "Excluir grupo",
      message: `Você tem certeza que deseja excluir o grupo "${row.id}"?`,
      onClose: () => {
        closeConfirmationDialog();
      },
      onDisagree: () => {
        closeConfirmationDialog();
      },
      onAgree: () => {
        deleteGroup(row.id);
        closeConfirmationDialog();
      },
    });
  }

  function showMultiplesDeleteConfirmation(rows) {
    setConfirmationDialogData({
      open: true,
      title: "Excluir grupos selecionados",
      message: `Você tem certeza que deseja excluir os grupos selecionados?`,
      onClose: () => {
        closeConfirmationDialog();
      },
      onDisagree: () => {
        closeConfirmationDialog();
      },
      onAgree: () => {
        deleteMultipleGroups(rows);
        closeConfirmationDialog();
      },
    });
  }

  function handleClickRowID(id) {
    history.push(`/grupos/${id}`);
  }

  function handleClickEditRow(row) {
    history.push(`/grupos/${row.id}`);
  }

  function handleClickDeleteRow(row) {
    showDeleteConfirmation(row);
  }

  function handleClickRefresh() {
    loadGroups();
  }

  function handleClickAdd() {
    history.push("/grupos/novo");
  }

  function handleSelectRow(rows) {
    setSelectedRows(rows);
  }

  function handleDeleteRows(rows) {
    showMultiplesDeleteConfirmation(rows);
  }

  useEffect(() => {
    loadGroups();
  }, []);

  return (
    <AdminPageTemplate breadcrumbs={props.breadcrumbs}>
      <PageBody title="Grupos">
        {/* table */}
        <CrudTable
          columns={columns}
          rows={rows}
          loading={loading}
          filter={true}
          filterFields={[
            {
              type: "text",
              name: "id",
              label: "Código de Identificação",
            },
            {
              type: "text",
              name: "name",
              label: "Nome",
            },
          ]}
          selectedRows={selectedRows}
          rowsSelection={true}
          onRefreshRows={handleClickRefresh}
          onSelectRow={handleSelectRow}
          onDeleteRows={handleDeleteRows}
        />
        {/* end table */}
        {/* add button */}
        <Fab
          onClick={handleClickAdd}
          color="primary"
          aria-label="add"
          className={classes.control}
        >
          <AddIcon />
        </Fab>
        {/* end add button */}
      </PageBody>

      {/* confirmation dialog */}
      <ConfirmationDialog {...confirmationDialogData} />
      {/* end confirmation dialog */}
    </AdminPageTemplate>
  );
}

Groups.propTypes = {
  breadcrumbs: PropTypes.array,
};

Groups.defaultProps = {
  breadcrumbs: [],
};

export default Groups;
